import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import Paginator from '../../../util_components/paginator';

import { day_format_year, time_format, date_drop_for_report } from '../../../../constants';
import { get_api } from '../../../../redux/api_funcs';
import * as actions from '../../../../redux/action_creators';
import { get_api_url } from '../../../../utils/urls';
import EventsModal from './events_modal';
import {
  parse_filters,
  build_search,
  offset_for_page,
  utc_window,
  PAGE_SIZE,
  DEFAULT_DATE_FILTER
} from './filters';
import {
  step_label,
  category_label,
  fmt_amount,
  fmt_datetime,
  reason_label,
  student_label,
  actionability_label,
  category_tone,
  origin_tone,
  origin_label,
  membership_label,
  membership_tone,
  membership_state,
  recovery_kind,
  tone_style
} from './helpers';

import 'react-datepicker/dist/react-datepicker.css';
import './payment_failures.css';

class PaymentFailures extends Component {
  constructor(props) {
    super(props);
    // The URL is the source of truth on entry, so a shared link lands on the same filters.
    this.state = {
      loading: true,
      error: false,
      rows: [],
      total: 0,
      attempt_total: 0,
      open_group: null,
      ...parse_filters(props.location.search, moment().format('YYYY-MM-DD'))
    };
  }

  componentDidMount() {
    this.load_data();
  }

  // Mirrors filters into the URL; replace, not push, so paging doesn't fill the back button.
  sync_url = () => {
    const { date_filter_type, start_date, end_date, page } = this.state;
    const search = build_search({ date_filter_type, start_date, end_date, page });
    if (search !== this.props.location.search) {
      this.props.history.replace({ pathname: this.props.location.pathname, search });
    }
  };

  load_data = (page = this.state.page) =>
    this.setState({ loading: true, error: false, page }, () => {
      this.sync_url();
      const { date_filter_type, start_date, end_date } = this.state;

      // Real UTC instants with an exclusive end — the presets disagree about whether their end is
      // inclusive, so normalising here is what keeps a range from returning an extra day.
      const window = utc_window(date_filter_type, start_date, end_date, this.props.iana_timezone);
      const qp = new URLSearchParams();
      qp.set('start_date', window.start);
      qp.set('end_date', window.end);
      qp.set('limit', String(PAGE_SIZE));
      qp.set('offset', String(offset_for_page(page)));

      const url = get_api_url(`/v2/cms_user/payment_failures?${qp.toString()}`, false, true);

      get_api(url, true)
        .then((res) => {
          const data = (res && res.data && res.data.data) || {};
          this.setState({
            loading: false,
            error: false,
            rows: data.rows || [],
            // total is the student count and drives paging; attempts is how many failures they hold.
            total: data.total || 0,
            attempt_total: data.attempt_total || 0
          });
        })
        .catch((e) => {
          let message;
          try {
            message = e.response.data.message;
          } catch (err) {
            message = 'Some error occured. Please contact dev team.';
          }
          if (typeof message !== 'string') message = 'Some error occured. Please contact dev team.';
          this.props.set_notification_variable(true, 'error', message);
          // Rows and total are left alone: an empty table here would read as "no failures", which
          // is the one thing this report must never say when it does not know.
          this.setState({ loading: false, error: true });
        });
    });

  // The select's own blank option is one click away and would otherwise send an empty filter type.
  change_handler = (e) =>
    this.setState({ [e.target.name]: e.target.value || DEFAULT_DATE_FILTER, page: 1 }, () =>
      this.load_data(1)
    );

  handle_date_change = (key, value) =>
    this.setState({ [key]: moment(value).format('YYYY-MM-DD'), page: 1 }, () => this.load_data(1));

  render_filters = () => {
    const { date_filter_type, start_date, end_date } = this.state;
    return (
      <div className="flex flex-wrap items-end gap-3 justify-center mb-3">
        <CustomSingleSelect
          label="Date range"
          options={date_drop_for_report}
          name="date_filter_type"
          onChange={this.change_handler}
          value={date_filter_type}
          style={{ width: 170 }}
        />
        {date_filter_type === 'CUSTOM' ? (
          <>
            <CustomDatePicker
              label="Start Date"
              value={start_date}
              handleChange={(val) => this.handle_date_change('start_date', val)}
              maxDate={end_date}
            />
            <CustomDatePicker
              label="End Date"
              value={end_date}
              handleChange={(val) => this.handle_date_change('end_date', val)}
              minDate={start_date}
              maxDate={moment().format('YYYY-MM-DD')}
            />
          </>
        ) : null}
      </div>
    );
  };

  // One row per student, showing their latest attempt; the icon opens every attempt behind it.
  // Category and origin carry their tone so an actionable failure is visible without reading the cell.
  render_rows = () =>
    this.state.rows.map((row) => (
      <div className="ct-row" key={row.group_id}>
        <div className="ct-col ct-hf ct-la ct-sm-font ct-bw">
          {row.student_uuid ? (
            <a
              href={`/studentprofile?id=${encodeURIComponent(row.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open student profile in a new tab"
            >
              {student_label(row)}
            </a>
          ) : (
            '—'
          )}
        </div>
        <div className="ct-col ct-1f ct-sm-font">{row.event_count}</div>
        <div className="ct-col ct-hf ct-sm-font">{step_label(row.step)}</div>
        <div className="ct-col ct-hf ct-sm-font ct-bw">
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{reason_label(row)}</span>
        </div>
        <div className="ct-col ct-hf ct-sm-font">
          <span
            style={{
              ...tone_style(category_tone(row.error_category)),
              whiteSpace: 'normal',
              display: 'inline-block'
            }}
          >
            {category_label(row.error_category)}
          </span>
          <div className="ct-vsm-font" style={{ color: 'var(--text-muted)', marginTop: 2 }}>
            {actionability_label(row.error_category)}
          </div>
        </div>
        <div className="ct-col ct-qf ct-sm-font">{fmt_amount(row.amount, row.currency)}</div>
        <div className="ct-col ct-1f ct-sm-font">{row.country || '—'}</div>
        <div className="ct-col ct-hf ct-vsm-font">{this.render_origins(row.origins)}</div>
        <div className="ct-col ct-hf ct-sm-font">{this.render_membership(row)}</div>
        <div className="ct-col ct-hf ct-sm-font">
          {fmt_datetime(row.last_attempt, this.props.iana_timezone, day_format_year)}
          <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
            {fmt_datetime(row.last_attempt, this.props.iana_timezone, time_format)}
          </div>
        </div>
        <div className="ct-col pf-actions">
          <button
            type="button"
            title="Show every attempt by this student"
            aria-label="Show attempts"
            onClick={() => this.setState({ open_group: row })}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: 'var(--text-muted)'
            }}
          >
            &#9432;
          </button>
        </div>
      </div>
    ));

  // Did a membership payment land after this student's last failure? The sub-line separates a
  // recovered renewal from a first-time signup — both resolve the failure, differently.
  render_membership = (row) => (
    <>
      <span
        style={{
          ...tone_style(membership_tone(row)),
          whiteSpace: 'normal',
          display: 'inline-block'
        }}
      >
        {membership_label(row)}
      </span>
      {membership_state(row) === 'recovered' ? (
        <div className="ct-vsm-font" style={{ color: 'var(--text-muted)', marginTop: 2 }}>
          {fmt_datetime(row.recovered_at, this.props.iana_timezone, day_format_year)}
          {' · '}
          {recovery_kind(row)}
        </div>
      ) : null}
    </>
  );

  // One badge per writer, so a row seen by both our servers and Stripe reads at a glance.
  render_origins = (origins) =>
    String(origins || '')
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean)
      .map((o) => (
        <span
          key={o}
          style={{
            ...tone_style(origin_tone(o)),
            whiteSpace: 'normal',
            display: 'inline-block',
            marginRight: 4
          }}
        >
          {origin_label(o)}
        </span>
      ));

  // Both numbers, because a row is a student: four declines from one person is one row, four tries.
  // A stat line rather than a sentence — the counts are what Care reads, not the grammar round them.
  render_summary = () => {
    const { total, attempt_total } = this.state;
    // An older server sends no attempt count; the student count is the honest floor.
    const attempts = attempt_total || total;
    const stat = (value, label) => (
      <>
        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value}</span>{' '}
        {value === 1 ? label : `${label}s`}
      </>
    );
    return (
      <div className="text-center mb-3" style={{ color: 'var(--text-muted)' }}>
        {stat(attempts, 'failed attempt')}
        <span style={{ margin: '0 10px' }}>·</span>
        {stat(total, 'student')}
      </div>
    );
  };

  render_body = () => {
    const { error, rows, total, page } = this.state;

    // An error must not look like a quiet day: this is the screen Care checks to find out whether
    // payments are breaking.
    if (error) {
      return (
        <div className="text-center" style={{ color: 'var(--state-error-text)' }}>
          Could not load payment failures. This is a load error, not a count of zero — retry, and
          raise it with the dev team if it persists.
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <>
          {this.render_summary()}
          <div className="text-center">No failures in this range.</div>
        </>
      );
    }

    return (
      <>
        {this.render_summary()}
        <div className="cus-table pf-table">
          <div className="ct-row ct-h">
            <div className="ct-col ct-hf ct-la ct-sm-font">Student</div>
            <div className="ct-col ct-1f ct-sm-font">Tries</div>
            <div className="ct-col ct-hf ct-sm-font">Step</div>
            <div className="ct-col ct-hf ct-sm-font">Reason</div>
            <div className="ct-col ct-hf ct-sm-font">Category</div>
            <div className="ct-col ct-qf ct-sm-font">Amount</div>
            <div className="ct-col ct-1f ct-sm-font">Country</div>
            <div className="ct-col ct-hf ct-sm-font">Seen by</div>
            <div className="ct-col ct-hf ct-sm-font">Member</div>
            <div className="ct-col ct-hf ct-sm-font">Last attempt</div>
            <div className="ct-col pf-actions" />
          </div>
          {this.render_rows()}
        </div>
        <Paginator
          curr_page={page}
          page_limit={Math.ceil(total / PAGE_SIZE)}
          pageChange={(p) => this.load_data(p)}
        />
      </>
    );
  };

  render() {
    const { loading, open_group } = this.state;
    return (
      <div className="common-page-container">
        <CommonHeader title="Payment Failures" />
        <div className="common-page-content">
          {this.render_filters()}
          {loading ? <div className="text-center">Loading…</div> : this.render_body()}
        </div>
        {open_group ? (
          <EventsModal
            group={open_group}
            iana_timezone={this.props.iana_timezone}
            start_date={this.state.start_date}
            end_date={this.state.end_date}
            date_filter_type={this.state.date_filter_type}
            set_notification_variable={this.props.set_notification_variable}
            close={() => this.setState({ open_group: null })}
          />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ iana_timezone: state.home.iana_timezone });

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PaymentFailures));
